import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BellRing, Search } from 'lucide-react';
import { COMM_DEPARTMENT_LABEL, ROSTER_ROLE_LABEL, formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem } from '../bookings/util.js';
import { Meter } from './Sent.jsx';

// Communications > Recipient activity: everyone who's been sent a message,
// with how much they read and acknowledge, those owing the most first.
// /comms/people/:personId is one person's messages.
export default function Activity() {
  const { personId } = useParams();
  if (personId) return <Person personId={personId} />;
  return <People />;
}

const SORTS = {
  outstanding: (a, b) => b.outstanding - a.outstanding || a.name.localeCompare(b.name, 'en-GB'),
  name: (a, b) => a.name.localeCompare(b.name, 'en-GB'),
  read: (a, b) => a.read / a.received - b.read / b.received || b.outstanding - a.outstanding,
};

function People() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('outstanding');
  useEffect(() => { api('/comms/people').then(setList, (e) => setError(problem(e))); }, []);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list || []).filter((p) => !s || `${p.name} ${p.email}`.toLowerCase().includes(s)).sort(SORTS[sort]);
  }, [list, q, sort]);
  const owing = (list || []).filter((p) => p.outstanding > 0).length;

  return (
    <div className="stack">
      <section className="card">
        <h1>Recipient activity</h1>
        <p className="muted">Everyone who has been sent a message: how many they&apos;ve read and acknowledged, and how many they still owe. Open someone to see their messages and remind them.</p>
        {list && (
          <div className="stat-row comm-stats">
            <div className="stat"><span className="stat-num">{list.length}</span><span className="stat-label">People sent messages</span></div>
            <div className={`stat${owing ? ' stat-warn' : ''}`}><span className="stat-num">{owing}</span><span className="stat-label">Still owe something</span></div>
          </div>
        )}
      </section>
      <section className="card">
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!list && !error && <p className="muted">Loading…</p>}
        {list && !list.length && <p className="muted">No messages sent yet.</p>}
        {list && list.length > 0 && (
          <>
            <div className="filters">
              <label className="search">
                <Search size={18} aria-hidden="true" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" aria-label="Search people" />
              </label>
              <select className="popup-input picker-filter" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
                <option value="outstanding">Most owed first</option>
                <option value="read">Least read first</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div className="table-scroll">
              <table className="table clickable">
                <thead>
                  <tr><th>Name</th><th>Received</th><th>Read</th><th>Acknowledged</th><th>Still owed</th><th>Last read</th></tr>
                </thead>
                <tbody>
                  {shown.map((p) => (
                    <tr key={p.personId} tabIndex={0} className={p.active ? '' : 'inactive'} onClick={() => navigate(`/comms/people/${p.personId}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/comms/people/${p.personId}`); }}>
                      <td>
                        <strong>{p.name}</strong>
                        <div className="small muted">{p.email}{p.role ? ` · ${ROSTER_ROLE_LABEL[p.role] || p.role}` : ''}{p.active ? '' : ' · No longer active'}</div>
                      </td>
                      <td>{p.received}</td>
                      <td><Meter n={p.read} of={p.received} /></td>
                      <td>{p.ackNeeded ? <Meter n={p.acked} of={p.ackNeeded} /> : <span className="muted small">None asked</span>}</td>
                      <td>{p.outstanding ? <span className="status status-cancelled">{p.outstanding}</span> : <span className="status status-completed">None</span>}</td>
                      <td>{p.lastReadAt ? formatDateTime(p.lastReadAt) : <span className="muted">Never</span>}</td>
                    </tr>
                  ))}
                  {!shown.length && <tr><td colSpan={6} className="muted">Nobody matches.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Person({ personId }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api(`/comms/people/${encodeURIComponent(personId)}`).then(setP, (e) => setError(problem(e)));
  }, [personId]);
  useEffect(load, [load]);

  const back = <Link to="/comms/people" className="link-btn back-link page-back"><ArrowLeft size={18} aria-hidden="true" /> Recipient activity</Link>;
  if (!p) return <div className="stack">{back}<section className="card">{error ? <p className="notice-line notice-bad">{error}</p> : <p className="muted">Loading…</p>}</section></div>;

  const owes = (m) => !m.readAt || (m.requiresAck && !m.ackedAt);
  const owed = p.messages.filter(owes);
  const read = p.messages.filter((m) => m.readAt).length;
  const asked = p.messages.filter((m) => m.requiresAck);
  const acked = asked.filter((m) => m.ackedAt).length;
  const remind = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api(`/comms/people/${encodeURIComponent(personId)}/remind`, { method: 'POST', body: {} });
      setNote({ tone: 'ok', text: `Reminded about ${r.reminded} ${r.reminded === 1 ? 'message' : 'messages'}.` });
      load();
    } catch (e) {
      setNote({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(false);
    }
  };
  const status = (m) => (m.ackedAt ? 'Acknowledged' : m.readAt ? (m.requiresAck ? 'Read, not acknowledged' : 'Read') : 'Not read');

  return (
    <div className="stack">
      {back}
      <section className="card">
        <div className="page-head">
          <div>
            <h1>{p.name}</h1>
            <p className="muted">{p.email}{p.role ? ` · ${ROSTER_ROLE_LABEL[p.role] || p.role}` : ''}{p.active ? '' : ' · No longer active'}</p>
          </div>
          <button type="button" className="btn primary with-icon" disabled={busy || !owed.length} onClick={remind}>
            <BellRing size={16} aria-hidden="true" /> {owed.length ? `Remind about ${owed.length} owed` : 'Nothing owed'}
          </button>
        </div>
        <div className="stat-row comm-stats">
          <div className="stat"><span className="stat-num">{p.messages.length}</span><span className="stat-label">Received</span></div>
          <div className="stat"><span className="stat-num">{Math.round((read / p.messages.length) * 100)}%</span><span className="stat-label">Read ({read})</span></div>
          {asked.length > 0 && <div className="stat"><span className="stat-num">{Math.round((acked / asked.length) * 100)}%</span><span className="stat-label">Acknowledged ({acked} of {asked.length})</span></div>}
          <div className={`stat${owed.length ? ' stat-warn' : ''}`}><span className="stat-num">{owed.length}</span><span className="stat-label">Still owed</span></div>
        </div>
        {note && <p className={`notice-line notice-${note.tone}`} role="status">{note.text}</p>}
      </section>
      <section className="card">
        <h2>Messages</h2>
        <div className="table-scroll">
          <table className="table clickable">
            <thead>
              <tr><th>Message</th><th>Sent</th><th>Status</th><th>Read</th><th>Acknowledged</th><th>Reminders</th></tr>
            </thead>
            <tbody>
              {p.messages.map((m) => (
                <tr key={m.id} tabIndex={0} onClick={() => navigate(`/comms/${m.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/comms/${m.id}`); }}>
                  <td><strong>{m.subject}</strong><div className="small muted">{COMM_DEPARTMENT_LABEL[m.department]}{m.priority === 'urgent' ? ' · Urgent' : ''}</div></td>
                  <td>{formatDateTime(m.sentAt)}<div className="small muted">{m.sentByName}</div></td>
                  <td><span className={`status ${!owes(m) ? 'status-completed' : m.readAt ? 'status-on_the_way' : 'status-cancelled'}`}>{status(m)}</span></td>
                  <td>{m.readAt ? formatDateTime(m.readAt) : <span className="muted">-</span>}</td>
                  <td>{m.requiresAck ? (m.ackedAt ? formatDateTime(m.ackedAt) : <span className="muted">-</span>) : <span className="muted small">Not asked</span>}</td>
                  <td>{m.reminders ? `${m.reminders} (last ${formatDateTime(m.remindedAt)})` : <span className="muted">None</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
